import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react'
import useDebounceCallback from '../hooks/useDebounceCallback'
import { createStateFingerprint, areStateFingerprintsEqual } from '../utils/stateFingerprint'
import { Box, Typography, Button, IconButton, Tabs, Tab } from '@mui/material'
import ExpandIcon from './ExpandIcon'
import ChatInterface from './ChatInterface'
import ChatInterfaceQueueDriven from './ChatInterfaceQueueDriven'
import ChatInterfaceQueueDrivenSimple from './ChatInterfaceQueueDrivenSimple'
import ChatInterfaceElevenLabsIntake from './ChatInterfaceElevenLabsIntake'
import { USE_QUEUE_DRIVEN_DISCOVERY } from '../config/discoveryConfig'
import TabPanel from './TabPanel'
import MessagingFormTab from './MessagingFormTab'
import MatchSummary from './MatchSummary'
import SearchSummary from './SearchSummary'
import SampleCandidates from './SampleCandidates'
import CustomComponentRenderer from './CustomComponentRenderer'
import IntakeSummary from './IntakeSummary'
import EmptySearchStrategy from './EmptySearchStrategy'
import AIIcon from './AIIcon'
import ReviewMatchesTabV2 from './ReviewMatchesTabV2'
import { COLORS, COMPONENT_COLORS, UI } from '../config/colors'

const MainContent = ({
  isWaitingForResponse,
  leftSidebarOpen,
  rightSidebarOpen,
  currentAIMessage,
  matchResults,
  queries,
  handleRefreshResults,
  handleRefreshResultsFromQueue,
  matchesLoading,
  onSaveSearch,
  onOpenFindemSearch,
  onSendText,
  onSendVoice,
  onStopVoice,
  isRecording,
  matchCount,
  messages,
  intakeMode,
  handleStartIntake,
  convoStarted,
  currentDiscoveryState,
  advanceToNextTopic, // This function needs to be modified to handle keepCompanyStageVisible
  setMessages,
  experienceHistogramData,
  levelCounts,
  locationMap,
  locationKeys,
  companyStageData,
  companyBusinessModelData,
  titleKeys,
  setTitleKeys,
  selectedSkills,
  setSelectedSkills,
  confirmedSkills,
  setConfirmedSkills,
  icpRequirements,
  drawerWidth,
  collapsedWidth,
  userInfo,
  userId, // Add userId prop
  onToggle,
  sessionName = 'Untitled Session', // Add sessionName prop with default value
  currentSessionId, // Add currentSessionId prop
  sessionSearchId, // ICP ID for Findem search link
  onToggleCandidateView, // Add callback prop
  updateDiscoveryState // Add updateDiscoveryState prop
}) => {
  const [profiles, setProfiles] = useState(null)
  // State to store macro mapping data for displaying matching criteria
  const [macroMappingData, setMacroMappingData] = useState(null)
  // State to track the active tab
  const [activeTab, setActiveTab] = useState(0);
  // State to track if a fetch is in progress
  const [isFetchingMatches, setIsFetchingMatches] = useState(false);
  // Refs to track message changes and state fingerprints
  const messageChangeRef = useRef(false);
  const lastStateFingerprintRef = useRef(null);
  
  // Company stage state management
  const [selectedCompanyStages, setSelectedCompanyStages] = useState([]);
  const [confirmedCompanyStages, setConfirmedCompanyStages] = useState([]);
  
  // Candidate viewing mode state
  const [candidateViewMode, setCandidateViewMode] = useState(false); // Toggle between search and candidate view
  const [currentCandidateIndex, setCurrentCandidateIndex] = useState(0);
  const [candidateFeedbackMessages, setCandidateFeedbackMessages] = useState([]); // Store feedback messages
  const [originalMessages, setOriginalMessages] = useState([]); // Store original search messages
  const [manuallyExitedCandidateView, setManuallyExitedCandidateView] = useState(false); // Track manual exits
  
  // Messaging form state - persistent across tab switches
  const [messagingFormData, setMessagingFormData] = useState({
    interviewCount: 5,
    roleInfo: '',
    companyExcitement: '',
    timing: '',
    managerAppeal: '',
    prescreeningQuestions: [
      { question: '', acceptableAnswers: ['', '', ''] }
    ],
    scheduling: ''
  });
  
  
  // Get the selected role title from titleKeys (if available)
  const selectedRoleTitle = titleKeys && titleKeys.length > 0 ? titleKeys[0] : null;

  // Debug messages prop changes
  useEffect(() => {
    console.log(`🔄 MAINCONTENT RECEIVED: ${messages.length} messages, ${messages.filter(msg => msg.sessionRestored === true).length} readonly`);
    console.log(`🔄 CANDIDATE VIEW MODE: ${candidateViewMode}`);
    console.log(`🔄 CANDIDATE FEEDBACK MESSAGES: ${candidateFeedbackMessages.length}`);
  }, [messages, candidateViewMode, candidateFeedbackMessages]);



  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Switch to messaging tab
  const switchToMessaging = () => {
    console.log('MainContent.switchToMessaging - Switching to messaging tab');
    setActiveTab(1); // Switch to tab index 1 (Messaging tab)
  };

  // Switch to candidates tab
  const switchToCandidates = () => {
    console.log('MainContent.switchToCandidates - Switching to candidates tab');
    setActiveTab(0); // Switch to tab index 0 (Candidates tab)
  };

  // Toggle candidate view mode
  const toggleCandidateViewMode = () => {
    if (!candidateViewMode) {
      // Entering candidate view mode
      setOriginalMessages([...messages]); // Store current messages
      setCandidateViewMode(true);
      setCurrentCandidateIndex(0);
      setManuallyExitedCandidateView(false); // Reset manual exit flag
      
      // Update discovery state to highlight "Review Matches" tab
      if (typeof updateDiscoveryState === 'function') {
        updateDiscoveryState({ 
          ...currentDiscoveryState, 
          currentDiscoTopic: 'candidate_preview' 
        });
      }
    } else {
      // Exiting candidate view mode - merge feedback messages back into original messages
      const mergedMessages = [...originalMessages];
      
      // Add candidate feedback messages as read-only messages
      if (candidateFeedbackMessages.length > 0) {
        candidateFeedbackMessages.forEach(feedbackMsg => {
          mergedMessages.push({
            ...feedbackMsg,
            isReadOnly: true, // Mark as read-only
            candidateFeedback: true // Mark as candidate feedback
          });
        });
      }
      
      setMessages(mergedMessages);
      setCandidateViewMode(false);
      setCandidateFeedbackMessages([]); // Clear feedback messages
      setManuallyExitedCandidateView(true); // Mark as manually exited
      
      // Update discovery state back to define role when exiting candidate view
      if (typeof updateDiscoveryState === 'function') {
        updateDiscoveryState({ 
          ...currentDiscoveryState, 
          currentDiscoTopic: null 
        }); // or whatever represents the define role state
      }
    }
  };

  // Always go to intake view (not toggle) - for "Define Role" button
  const goToIntakeView = () => {
    console.log('MainContent.goToIntakeView called - forcing switch to intake view');
    console.log('Current candidateViewMode:', candidateViewMode);
    
    // Always force switch to intake view, regardless of current state
    if (candidateViewMode) {
      // If we're in candidate view, merge feedback messages
      const mergedMessages = [...originalMessages];
      
      // Add candidate feedback messages as read-only messages
      if (candidateFeedbackMessages.length > 0) {
        candidateFeedbackMessages.forEach(feedbackMsg => {
          mergedMessages.push({
            ...feedbackMsg,
            isReadOnly: true, // Mark as read-only
            candidateFeedback: true // Mark as candidate feedback
          });
        });
      }
      
      setMessages(mergedMessages);
      setCandidateFeedbackMessages([]); // Clear feedback messages
    }
    
    // Always force these states regardless of previous state
    setCandidateViewMode(false);
    setManuallyExitedCandidateView(true); // Mark as manually exited
    
    // Let ChatInterfaceQueueDrivenSimple handle the discovery state transition automatically
    // It will check completed queue items and advance to the next appropriate topic
    
    console.log('MainContent.goToIntakeView completed - candidateViewMode should now be false');
  };

  // Handle candidate feedback text input
  const handleCandidateFeedback = (feedbackText) => {
    if (!feedbackText.trim()) return;
    
    const profileIds = profiles ? Object.keys(profiles) : [];
    const currentProfileId = profileIds[currentCandidateIndex];
    const currentProfile = profiles?.[currentProfileId];
    
    const feedbackMessage = {
      role: 'User',
      content: feedbackText,
      id: `candidate_feedback_${Date.now()}`,
      timestamp: new Date().toISOString(),
      candidateId: currentProfileId,
      candidateName: currentProfile ? `${currentProfile.first_name} ${currentProfile.last_name}` : 'Unknown'
    };
    
    setCandidateFeedbackMessages(prev => [...prev, feedbackMessage]);
  };

  // Create a fingerprint of the current state
  const currentStateFingerprint = useMemo(() => {
    return createStateFingerprint(messages, matchResults, icpRequirements);
  }, [messages, matchResults, icpRequirements]);

  // 1. Separate useEffect for tab reset when switching conversations
  useEffect(() => {
    // Tab selection based on discovery state
    if (currentDiscoveryState?.currentDiscoTopic === 'candidate_preview') {
      // Review Matches tab (within tab index 0) when in candidate preview mode
      setActiveTab(0);
      console.log("Setting Review Matches tab for candidate preview mode");
    } else if (currentDiscoveryState?.currentDiscoTopic === 'messaging') {
      // Switch to messaging tab (index 1) when in messaging mode
      setActiveTab(1);
      console.log("Setting Send Outreach tab for messaging mode");
    } else if (currentDiscoveryState?.currentDiscoTopic && 
               currentDiscoveryState.currentDiscoTopic !== 'candidate_preview' && 
               currentDiscoveryState.currentDiscoTopic !== 'messaging') {
      // Define Role tab (within tab index 0) for all discovery topics (skills, experience, etc.)
      setActiveTab(0);
      console.log("Setting Define Role tab for discovery topic:", currentDiscoveryState.currentDiscoTopic);
    } else if (currentDiscoveryState === null) {
      // Default to Define Role tab when no discovery state (new session)
      setActiveTab(0);
      console.log("Setting Define Role tab for new session (null discovery state)");
    }
  }, [currentDiscoveryState?.currentDiscoTopic, currentDiscoveryState]); // Depend on both topic and state
  
  // 2. Track when messages change
  useEffect(() => {
    messageChangeRef.current = true;
    
    // Cleanup function to reset the flag after the next render cycle
    return () => {
      setTimeout(() => {
        messageChangeRef.current = false;
      }, 0);
    };
  }, [messages]);
  
  // 3. Auto-switch to candidate view mode when topic is candidate_preview
  useEffect(() => {
    if (currentDiscoveryState?.currentDiscoTopic === 'candidate_preview' && !candidateViewMode && !manuallyExitedCandidateView) {
      console.log('MainContent - Auto-switching to candidate view mode for candidate_preview topic');
      setCandidateViewMode(true);
      setCurrentCandidateIndex(0);
      setOriginalMessages([...messages]); // Store current messages
    }
    // Reset manual exit flag when topic changes away from candidate_preview
    else if (currentDiscoveryState?.currentDiscoTopic !== 'candidate_preview' && manuallyExitedCandidateView) {
      console.log('MainContent - Resetting manual exit flag as topic changed away from candidate_preview');
      setManuallyExitedCandidateView(false);
    }
  }, [currentDiscoveryState?.currentDiscoTopic, candidateViewMode, messages, manuallyExitedCandidateView]);

  // Debounced fetchMatches function
  const debouncedFetchMatchesCallback = useCallback(async () => {
    // Skip if we're already fetching
    if (isFetchingMatches) {
      console.log('Skipping fetchMatches - already fetching');
      return;
    }
    
    // Skip if the state hasn't changed since last fetch
    if (lastStateFingerprintRef.current && 
        areStateFingerprintsEqual(lastStateFingerprintRef.current, currentStateFingerprint)) {
      console.log('Skipping fetchMatches - state has not changed');
      return;
    }
    
    console.log('Executing debounced fetchMatches');
    setIsFetchingMatches(true);
    
    try {
      await fetchMatches();
      // Update the fingerprint after successful fetch
      lastStateFingerprintRef.current = { ...currentStateFingerprint };
    } finally {
      setIsFetchingMatches(false);
    }
  }, [currentStateFingerprint, isFetchingMatches]);
  
  // Create the debounced function
  const debouncedFetchMatches = useDebounceCallback(debouncedFetchMatchesCallback, 1000);
  
  // DISABLED: Automatic fetchMatches - only call fetchMatches when user clicks "View Candidates"
  // The right sidebar should only use runMultiMatches for counts, not fetchMatches for profiles
  /*
  useEffect(() => {
    // Only proceed if we have the necessary data
    if (!matchResults) return;
    
    // Skip during message changes to avoid race conditions
    if (messageChangeRef.current) return;
    
    // Trigger the debounced fetch
    debouncedFetchMatches();
  }, [messages, matchResults, icpRequirements, debouncedFetchMatches]);
  */

  async function fetchMatches(queueItems = null) {
    return new Promise(async (resolve, reject) => {
      try {
        console.log('MainContent.fetchMatches - Starting fetch process');
        const url = 'https://pub.findem.ai/pub/api/matchesV2'
  
        const headers = {
          'content-type': 'application/json'
        }
  
        let requirements = [];
        
        // NEW: If queueItems are provided, use them to construct requirements
        if (queueItems && queueItems.length > 0) {
          console.log('MainContent.fetchMatches - Using queue items:', queueItems.length);
          
          // Filter only completed queue items (disco_done = true)
          const completedItems = queueItems.filter(item => item.disco_done === true);
          
          if (completedItems.length === 0) {
            console.warn('MainContent.fetchMatches - No completed queue items available!');
            resolve();
            return;
          }
          
          // Construct ICP requirements from completed queue items using only the 4 required fields
          requirements = completedItems.map(item => ({
            section_type: item.section_type || 'Must Have',
            section_name: item.section_name,
            section_id: item.section_id,
            bool_str: item.bool_str || ''
          }));
          
          console.log('MainContent.fetchMatches - Constructed requirements from queue items:', requirements.length);
        } else {
          // LEGACY: Use the requirements from matchResults
          // This ensures we're using exactly what's displayed in IntakeSummary
          requirements = matchResults?.search_gen_response?.icp_requirements || [];
          console.log('MainContent.fetchMatches - Using legacy matchResults requirements:', requirements.length);
        }
        
        // Skip if we don't have requirements
        if (requirements.length === 0) {
          console.warn('MainContent.fetchMatches - No requirements available!');
          resolve();
          return;
        }
        
        console.log('MainContent.fetchMatches - Using requirements:', requirements.length);
        
        // Log all requirements for debugging
        console.log('MainContent.fetchMatches - All requirements:', requirements.map(req => ({
          section_name: req.section_name,
          bool_str: req.bool_str,
          section_id: req.section_id
        })));
  
        const body = {
          start_index: 0,
          requirements: requirements,
          sid: null,
          detail: true,
          new_service: true,
          new_algo: false,
          include_stale: true,
          skinny_output: true,
          orchestrator: null,
          refetchToken: '',
          count: 5,
          use_optional_queries: false,
          channel: 'external',
          strict_pairing_mode: false,
          prioritize_freshness: true
        }
  
        console.log('MainContent.fetchMatches - Sending API request');
        const response = await fetch(url, {
          method: 'POST',
          body: JSON.stringify(body),
          credentials: 'include',
          headers: {
            'Content-type': 'application/json'
          },
          mode: 'cors'
        })
  
        if (!response.ok) {
          throw new Error(
            `Failed to fetch matches: ${response.status} ${response.statusText}`
          )
        }
  
        const data = await response.json()
        console.log('MainContent.fetchMatches - Response received:', data ? 'data exists' : 'null');

        // Store macro mapping data for displaying matching criteria
        if (data?.search_cxt?.auto_macro_map && data?.query_results) {
          const macroData = {
            autoMacroMap: data.search_cxt.auto_macro_map,
            queryResults: data.query_results,
            queries: queries // Store queries for hex lookup
          };
          setMacroMappingData(macroData);

          console.log('MainContent.fetchMatches - Stored macro mapping data:', macroData);
        }
        
        const prids = data?.query_results?.map(result => result.profile_id) || []
        console.log('MainContent.fetchMatches - Extracted profile IDs:', prids.length);
        
        if (prids.length > 0) {
          console.log('MainContent.fetchMatches - Profile IDs:', prids);
          console.log('MainContent.fetchMatches - Calling fetchProfiles');
          const profilesData = await fetchProfiles(prids);
          console.log('MainContent.fetchMatches - fetchProfiles completed:', 
            profilesData ? `data exists with ${Object.keys(profilesData).length} profiles` : 'null');
        } else {
          console.warn('MainContent.fetchMatches - No profile IDs returned from matches.');
        }
        
        // Successfully completed
        console.log('MainContent.fetchMatches - Process completed');
        resolve();
      } catch (error) {
        console.error('Error in fetchMatches:', error)
        // Still resolve the promise to ensure .finally() is called
        // This prevents the isFetchingMatches flag from getting stuck
        resolve();
      }
    });
  }

  async function fetchProfiles (profileIds) {
    return new Promise(async (resolve, reject) => {
      try {
        console.log('MainContent.fetchProfiles - Starting with IDs:', profileIds.length);
        const url = 'https://search.findem.ai/pub/api/profile'
  
        const headers = {
          'content-type': 'application/json'
        }
  
        const body = JSON.stringify({
          detail: 'true',
          prids: profileIds,
          bust_cache: false,
          type: 'Profiles'
        })
  
        console.log('MainContent.fetchProfiles - Sending API request');
        const response = await fetch(url, {
          method: 'POST',
          headers: headers,
          body: body,
          credentials: 'include'
        })
        if (!response.ok) {
          throw new Error(
            `Failed to fetch profiles: ${response.status} ${response.statusText}`
          )
        }
  
        const profilesData = await response.json()
        console.log('MainContent.fetchProfiles - Response received:', 
          profilesData ? `data exists with ${Object.keys(profilesData).length} profiles` : 'null');
        
        console.log('MainContent.fetchProfiles - Setting profiles state');
        setProfiles(profilesData)
        
        // Check if profiles state was updated
        setTimeout(() => {
          console.log('MainContent.fetchProfiles - Profiles state after update (async check):', 
            profiles ? `exists with ${Object.keys(profiles).length} profiles` : 'null');
        }, 0);
        
        resolve(profilesData);
      } catch (error) {
        console.error('Error in fetchProfiles:', error)
        // Still resolve the promise to ensure the chain continues
        resolve(null);
      }
    });
  }


  const actionLabels = !matchesLoading
    ? [
        {
          key: 'search_summary',
          label: 'Intake Summary',
          component: () => (
            <SearchSummary queries={queries} matchResults={matchResults}></SearchSummary>
          )
        },
        {
          key: 'total_matches',
          label: 'Insights',
          component: () => (
            <MatchSummary
              set_width={700}
              show_title={false}
              show_dei={false}
              show_legend={true}
              show_matches={true}
              matchCount={matchCount}
            ></MatchSummary>
          )
        },
        {
          key: 'dei_breakdown',
          label: 'DEI',
          component: () => (
            <MatchSummary
              set_width={300}
              show_title={false}
              show_dei={true}
              show_matches={false}
              matchCount={matchCount}
            ></MatchSummary>
          )
        },
        {
          key: 'example_candidates',
          label: 'Sample Matches',
          component: () => (
            <SampleCandidates candidates={profiles} macroMappingData={macroMappingData}></SampleCandidates>
          )
        },
        {
          key: 'findem_search_page',
          label: 'Findem Search',
          clickCb: onOpenFindemSearch || onSaveSearch
        }
      ]
    : []

  // State to track the right sidebar width
  const [rightSidebarWidth, setRightSidebarWidth] = useState(456); // Initial width from Figma design
  const [isDragging, setIsDragging] = useState(false);
  const dragStartXRef = useRef(0);
  const initialWidthRef = useRef(rightSidebarWidth);

  // Add event listeners for mouse move and mouse up when dragging starts
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDragging) {
        const deltaX = dragStartXRef.current - e.clientX;
        const newWidth = Math.max(300, Math.min(800, initialWidthRef.current + deltaX));
        setRightSidebarWidth(newWidth);
      }
    };
    
    const handleMouseUp = () => {
      if (isDragging) {
        setIsDragging(false);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
    };
    
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  // Handle mouse down on the dragger
  const handleDraggerMouseDown = (e) => {
    e.preventDefault(); // Prevent default behavior
    setIsDragging(true);
    dragStartXRef.current = e.clientX;
    initialWidthRef.current = rightSidebarWidth;
    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none'; // Prevent text selection during drag
  };

  // Pass the toggleCandidateViewMode and goToIntakeView functions to the parent component
  useEffect(() => {
    if (onToggleCandidateView && typeof onToggleCandidateView === 'function') {
      onToggleCandidateView(toggleCandidateViewMode, goToIntakeView);
    }
  }, [onToggleCandidateView, toggleCandidateViewMode, goToIntakeView]);

  // Expose functions and state globally for Header and ReviewMatchesTab to access
  useEffect(() => {
    window.switchToMessagingTab = switchToMessaging;
    window.goToIntakeView = goToIntakeView;
    window.toggleCandidateViewMode = toggleCandidateViewMode;
    window.isInCandidateView = candidateViewMode;
    // Expose a queue-driven matches refresher for candidate list
    window.fetchMatchesFromQueue = (queueItems) => {
      try { return fetchMatches(queueItems); } catch (e) { console.error('fetchMatchesFromQueue error:', e); return Promise.resolve(); }
    };
    // Expose the header navigation function for auto-navigation
    window.onNavigateToSendOutreach = () => {
      // Update discovery state to 'messaging' to highlight the header tab
      console.log('MainContent: onNavigateToSendOutreach called - updating discovery state to messaging');
      if (typeof updateDiscoveryState === 'function') {
        updateDiscoveryState({ 
          ...currentDiscoveryState, 
          currentDiscoTopic: 'messaging' 
        });
      } else {
        console.warn('MainContent: updateDiscoveryState function not available');
      }
    };
    
    // Cleanup function to remove the global references
    return () => {
      delete window.switchToMessagingTab;
      delete window.goToIntakeView;
      delete window.toggleCandidateViewMode;
      delete window.isInCandidateView;
      delete window.onNavigateToSendOutreach;
      delete window.fetchMatchesFromQueue;
    };
  }, [switchToMessaging, goToIntakeView, toggleCandidateViewMode, candidateViewMode, updateDiscoveryState, currentDiscoveryState]);

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'row', // Use row to create a 2-column layout
        flexGrow: 1,
        height: '100%',
        width: '100%',
        maxWidth: '100%',
        margin: '0 auto',
        transition: theme =>
          theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen
          }),
        bgcolor: '#FFFFFF', // White background color to match Figma design
        padding: 0,
        overflow: 'hidden',
        gap: '0px', // No gap between columns
      }}
    >
      {messages && messages.length > 0 ? (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          width: '100%',
          overflow: 'hidden',
          position: 'relative',
          backgroundColor: '#FFFFFF',
          maxWidth: '100%',
        }}
      >          
          {/* Tab Content */}
          <TabPanel value={activeTab} index={0} sx={{ 
            display: 'flex',
            flexDirection: 'row', // Changed to row to have chat and requirements side by side
            flexGrow: 1, 
            height: 'calc(100% - 48px)', // Subtract tab height only (no footer)
            padding: '0',
            overflow: 'hidden',
          }}>
            {/* Left Column: Conditionally render ChatInterface or ReviewMatchesTab */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
                height: '100%',
                width: `calc(100% - ${rightSidebarOpen ? rightSidebarWidth : 50}px)`,
                position: 'relative',
                overflow: 'hidden',
                transition: isDragging ? 'none' : 'width 0.1s ease',
              }}
            >
              {/* Conditionally render based on discovery state and candidate view mode */}
              {currentDiscoveryState?.currentDiscoTopic === 'candidate_preview' || candidateViewMode ? (
                /* Show ReviewMatchesTabV2 when in candidate preview mode */
                <ReviewMatchesTabV2
                  profiles={profiles}
                  macroMappingData={{
                    ...macroMappingData,
                    queries: queries
                  }}
                  onCandidateApproval={(candidateId, approved) => {
                    console.log('Candidate approval:', candidateId, approved);
                    // Handle candidate approval logic here
                  }}
                  handleRefreshResultsFromQueue={handleRefreshResultsFromQueue}
                  fetchMatches={fetchMatches}
                  fetchProfiles={fetchProfiles}
                />
              ) : (
                /* Show ChatInterface for intake view - conditionally use queue-driven or legacy */
                (() => {
                  // If Conversational Discovery is selected, render the ElevenLabs intake interface
                  if (intakeMode === 'elevenlabs') {
                    return (
                      <ChatInterfaceElevenLabsIntake
                        messages={messages}
                        setMessages={setMessages}
                        handleRefreshResults={handleRefreshResults}
                        convoStarted={convoStarted}
                        onSendVoice={onSendVoice}
                        onStopVoice={onStopVoice}
                        isRecording={isRecording}
                        isWaitingForResponse={isWaitingForResponse}
                      />
                    );
                  }

                  const ChatComponent = USE_QUEUE_DRIVEN_DISCOVERY ? ChatInterfaceQueueDrivenSimple : ChatInterface;
                  console.log(`🎯 MAINCONTENT: Using ${USE_QUEUE_DRIVEN_DISCOVERY ? 'Queue-Driven-Simple' : 'Legacy'} ChatInterface`);

                  return (
                    <ChatComponent
                      messages={candidateViewMode ? candidateFeedbackMessages : messages}
                      currentAIMessage={currentAIMessage}
                      isWaitingForResponse={isWaitingForResponse}
                      onSendText={onSendText}
                      onSendVoice={onSendVoice}
                      onStopVoice={onStopVoice}
                      isRecording={isRecording}
                      handleStartIntake={handleStartIntake}
                      convoStarted={convoStarted}
                      currentDiscoveryState={currentDiscoveryState}
                      advanceToNextTopic={advanceToNextTopic}
                      setMessages={setMessages}
                      experienceHistogramData={experienceHistogramData}
                      levelCounts={levelCounts}
                      locationMap={locationMap}
                      locationKeys={locationKeys}
                      companyStageData={companyStageData}
                      companyBusinessModelData={companyBusinessModelData}
                      titleKeys={titleKeys}
                      setTitleKeys={setTitleKeys}
                      selectedSkills={selectedSkills}
                      setSelectedSkills={setSelectedSkills}
                      confirmedSkills={confirmedSkills}
                      setConfirmedSkills={setConfirmedSkills}
                      selectedCompanyStages={selectedCompanyStages}
                      setSelectedCompanyStages={setSelectedCompanyStages}
                      confirmedCompanyStages={confirmedCompanyStages}
                      setConfirmedCompanyStages={setConfirmedCompanyStages}
                      icpRequirements={icpRequirements}
                      candidateViewMode={candidateViewMode}
                      currentCandidateIndex={currentCandidateIndex}
                      setCurrentCandidateIndex={setCurrentCandidateIndex}
                      profiles={profiles}
                      onCandidateFeedback={handleCandidateFeedback}
                      onToggleCandidateView={toggleCandidateViewMode}
                      switchToMessaging={switchToMessaging}
                      handleRefreshResults={handleRefreshResults}
                      handleRefreshResultsFromQueue={handleRefreshResultsFromQueue}
                      sessionId={currentSessionId}
                      userId={userId}
                      macroMappingData={{
                        ...macroMappingData,
                        queries: queries
                      }}
                      onSwitchToMessaging={switchToMessaging}
                      isMessagingMode={activeTab === 1}
                      initialRequirements={icpRequirements}
                      queries={queries}
                    />
                  );
                })()
              )}
            </Box>
            
            {/* Right Column: Requirements Panel - Only in Candidate Tab */}
            <Box
              sx={{
                width: rightSidebarOpen ? `${rightSidebarWidth}px` : '50px',
                height: '100%',
                backgroundColor: '#FFFFFF',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '20px',
                overflow: 'hidden',
                // Updated border to match Figma gradient design
                background: `
                  linear-gradient(#FFFFFF, #FFFFFF) padding-box,
                  linear-gradient(135deg, 
                    rgba(234, 236, 240, 1) 0%,
                    rgba(142, 121, 255, 0.3) 25%,
                    rgba(82, 197, 255, 0.3) 50%,
                    rgba(50, 225, 112, 0.3) 75%,
                    rgba(234, 236, 240, 1) 100%
                  ) border-box
                `,
                border: '1px solid transparent',
                position: 'relative',
                transition: isDragging ? 'none' : 'width 0.3s ease',
              }}
            >
              {/* Subtle Sidebar Dragger - only show when sidebar is open */}
              {rightSidebarOpen && (
                <Box
                  sx={{
                    position: 'absolute',
                    left: '-4px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '10px',
                    height: '60px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000,
                    cursor: 'ew-resize',
                    padding: '10px 7px',
                    '&:hover': {
                      '& > div': {
                        backgroundColor: '#969DAD',
                      },
                    },
                  }}
                  onMouseDown={handleDraggerMouseDown}
                >
                  <Box
                    sx={{
                      width: '4px',
                      height: '20px',
                      backgroundColor: '#DCDFEA',
                      borderRadius: '20px',
                      transition: 'background-color 0.2s ease',
                    }}
                  />
                </Box>
              )}
              
              {/* Expand button - only show when sidebar is collapsed */}
              {!rightSidebarOpen && (
                <Box
                  sx={{
                    width: '50px',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                  onClick={onToggle}
                >
                  <IconButton
                    size="small"
                  >
                    <ExpandIcon size={18} color="#465366" />
                  </IconButton>
                </Box>
              )}
              
              {/* Use IntakeSummary component dynamically - only show when sidebar is open */}
              {rightSidebarOpen && (
                <IntakeSummary
                  matchResults={matchResults}
                  matchCount={matchCount}
                  matchesLoading={matchesLoading}
                  handleRefreshResults={handleRefreshResults}
                  handleRefreshResultsFromQueue={handleRefreshResultsFromQueue}
                  queries={queries}
                  onSaveSearch={onSaveSearch}
                  onOpenFindemSearch={onOpenFindemSearch}
                  icpRequirements={icpRequirements}
                  userInfo={userInfo}
                  onToggle={onToggle}
                  messages={messages}
                  sessionName={sessionName}
                  sessionId={currentSessionId}
                  sessionSearchId={sessionSearchId}
                  selectedRoleTitle={selectedRoleTitle}
                  profiles={profiles}
                  onToggleCandidateView={toggleCandidateViewMode}
                  candidateViewMode={candidateViewMode}
                  currentDiscoveryState={currentDiscoveryState}
                  updateDiscoveryState={updateDiscoveryState}
                />
              )}
            </Box>
          </TabPanel>
          
          <TabPanel value={activeTab} index={1} sx={{ 
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1, 
            height: 'calc(100% - 48px)', // Subtract tab height only (no footer)
            padding: '0',
            overflow: 'hidden',
          }}>
            <Box sx={{ 
              flexGrow: 1, 
              height: '100%', // Full height for messaging tab
              width: '100%', // Full width for messaging tab
              overflow: 'hidden',
            }}>
              <MessagingFormTab 
                titleKeys={titleKeys}
                currentSessionId={currentSessionId}
                formData={messagingFormData}
                setFormData={setMessagingFormData}
                onSwitchToCandidates={switchToCandidates}
                userId={userId}
              />
            </Box>
          </TabPanel>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 4,
            py: 4,
            width: '100%',
            height: '100%',
            margin: '0',
            bgcolor: '#FFFFFF', // Updated background color to white to match Figma design
            color: '#111827', // Updated text color to dark for better contrast on white background
          }}
        >
          <Typography 
            variant='h4' 
            align='center' 
            color={COMPONENT_COLORS.mainContent.primary}
            sx={{
              fontFamily: UI.fontFamily.primary,
              fontSize: '24px',
              fontWeight: UI.fontWeight.bold,
              lineHeight: UI.lineHeight.large,
            }}
          >
            Welcome to CalibrateIQ
          </Typography>
          <Typography
            variant='h6'
            align='center'
            color={COMPONENT_COLORS.mainContent.secondary} // Using Figma color for secondary text
            sx={{ 
              mt: 2,
              fontFamily: UI.fontFamily.primary,
              fontSize: '18px',
              fontWeight: UI.fontWeight.medium,
              lineHeight: UI.lineHeight.medium,
            }}
          >
            Your Smart Assistant for Role Definition and Calibration
          </Typography>
          <Typography
            variant='body1'
            align='center'
            color={COMPONENT_COLORS.mainContent.secondary} // Using Figma color for secondary text
            sx={{ 
              mt: 2,
              fontFamily: UI.fontFamily.primary,
              fontSize: UI.fontSize.medium,
              fontWeight: UI.fontWeight.regular,
              lineHeight: UI.lineHeight.medium,
            }}
          >
            Capture job details, refine requirements, and get tailored
            insights—all through a seamless, guided process.
          </Typography>
          <Typography
            variant='body1'
            align='center'
            color={COMPONENT_COLORS.mainContent.secondary} // Using Figma color for secondary text
            sx={{ 
              mt: 1,
              fontFamily: UI.fontFamily.primary,
              fontSize: UI.fontSize.medium,
              fontWeight: UI.fontWeight.regular,
              lineHeight: UI.lineHeight.medium,
            }}
          >
            Define titles, skills, and preferences with clarity. CalibrateIQ
            tracks progress and ensures nothing is missed.
          </Typography>
          <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
            <Button
              variant='contained'
              color='primary'
              size='large'
              onClick={handleStartIntake}
              sx={{ 
                bgcolor: '#3B82F6',
                '&:hover': { bgcolor: '#2563EB' }
              }}
            >
              Start Fresh
            </Button>
            <Button
              variant='contained'
              color='primary'
              size='large'
              onClick={typeof window.handleStartWithJD === 'function' ? window.handleStartWithJD : undefined}
              sx={{ 
                bgcolor: '#3B82F6',
                '&:hover': { bgcolor: '#2563EB' }
              }}
            >
              Start with JD
            </Button>
            <Button
              variant='outlined'
              color='primary'
              size='large'
              onClick={typeof window.handleStartTopicBased === 'function' ? window.handleStartTopicBased : undefined}
              sx={{ 
                borderColor: '#EAECF0',
                color: '#101828',
                '&:hover': { borderColor: '#98A2B3', color: '#101828', backgroundColor: '#F9FAFB' }
              }}
            >
              Start From Scratch
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default MainContent
